import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useLive } from "../live";
import type { ReminderView, SessionUser } from "../types";
import { useMessage } from "./Message";
import { fundingLabel } from "./OpDayDialog";
import ReminderCard from "./ReminderCard";
import { BellIcon, dueText, ruDate, waitText } from "./reminders";

/** «Иванова Анна Петровна» → «Иванова А. П.» — подпись «кто поставил» в строке. */
function shortName(fio: string): string {
  const [last, ...rest] = fio.trim().split(/\s+/);
  return [last, ...rest.slice(0, 2).map((p) => `${p[0]}.`)].join(" ");
}

/**
 * Колокольчик в шапке — напоминания оперсписка у всех, кто его ведёт.
 *
 * Число — сколько напоминаний сработало (открыли день в нужном месяце,
 * наступила дата, месяцы прошли без дней). Щелчок — список: «Пора» —
 * сработавшие, «Ждут» — остальные; по дате постановки, кто раньше попросил —
 * выше. Щелчок по строке — карточка с телефоном и действиями.
 *
 * Список общий и живой: выполненное коллегой исчезает у всех за секунды
 * (событие сервера «reminders»), а когда напоминание срабатывает — открыли
 * день, — внизу появляется сообщение «Сработало напоминание» с кнопкой
 * «Открыть»: колокольчик в углу легко не заметить.
 */
export default function ReminderBell({
  user,
  mobile = false,
  onOpenPatient,
}: {
  user: SessionUser;
  mobile?: boolean;
  onOpenPatient?: (patId: number) => void;
}) {
  const message = useMessage();
  const [count, setCount] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ReminderView[] | null>(null);
  const [tab, setTab] = useState<"due" | "wait">("due");
  const [cardId, setCardId] = useState<number | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  /** Сколько было в прошлый раз — чтобы сказать «сработало», когда стало больше. */
  const seen = useRef<number | null>(null);

  const noticeGrowth = useCallback(
    (due: number) => {
      if (seen.current !== null && due > seen.current) {
        const n = due - seen.current;
        message(n === 1 ? "Сработало напоминание оперсписка" : `Сработало напоминаний оперсписка: ${n}`, "ok", {
          ms: 8000,
          action: {
            label: "Открыть",
            run: () => {
              setTab("due");
              setOpen(true);
            },
          },
        });
      }
      seen.current = due;
      setCount(due);
    },
    [message]
  );

  const loadCount = useCallback(async () => {
    try {
      noticeGrowth((await api.reminderCount()).due);
    } catch {
      /* колокольчик молчит, пока сервер недоступен */
    }
  }, [noticeGrowth]);

  const loadList = useCallback(async () => {
    try {
      const r = await api.reminders();
      setItems(r.reminders);
      noticeGrowth(r.due);
    } catch (err) {
      message(err as Error);
    }
  }, [message, noticeGrowth]);

  useEffect(() => {
    loadCount();
  }, [loadCount]);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  // Открыли день, коллега выполнил, наступила дата (опрос раз в минуту).
  useLive(["reminders", "op-lists"], () => void (open ? loadList() : loadCount()), { pollMs: 60_000 });

  // Щелчок мимо закрывает список — но не пока открыта карточка поверх него.
  useEffect(() => {
    if (!open || cardId !== null) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (box.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, cardId]);

  const due = (items ?? []).filter((v) => v.due);
  const wait = (items ?? []).filter((v) => !v.due);
  const shown = tab === "due" ? due : wait;

  return (
    <div className={`rem-bell-box${mobile ? " rem-bell-mobile" : ""}`} ref={box}>
      <div
        className={`rem-bell${count ? " has" : ""}${open ? " active" : ""}${mobile ? " top-mob-btn" : ""}`}
        title={count ? `Напоминания оперсписка: сработало ${count}` : "Напоминания оперсписка"}
        role="button"
        aria-expanded={open}
        onClick={() => {
          // Есть сработавшие — открываем на них.
          if (!open && count) setTab("due");
          setOpen((v) => !v);
        }}
      >
        <BellIcon size={mobile ? 20 : 18} />
        {!!count && <b className="rem-badge">{count > 99 ? "99+" : count}</b>}
      </div>

      {open && (
        <div className="rem-panel">
          <div className="rem-panel-head">
            <b>Напоминания</b>
            <div className="rem-tabs" role="tablist">
              <span role="tab" aria-selected={tab === "due"} className={tab === "due" ? "on" : undefined} onClick={() => setTab("due")}>
                Пора{due.length ? ` · ${due.length}` : ""}
              </span>
              <span role="tab" aria-selected={tab === "wait"} className={tab === "wait" ? "on" : undefined} onClick={() => setTab("wait")}>
                Ждут{wait.length ? ` · ${wait.length}` : ""}
              </span>
            </div>
          </div>

          <div className="rem-list">
            {items === null && <div className="rem-empty">Загружаем…</div>}
            {items !== null && !shown.length && (
              <div className="rem-empty">
                {tab === "due" ? "Сработавших напоминаний нет" : "Ждущих напоминаний нет"}
              </div>
            )}
            {shown.map((v) => {
              const other = v.held && v.held.user_id !== user.user_id ? v.held : null;
              const e = v.entry;
              return (
                <div key={v.rem_id} className={`rem-item${v.due ? " due" : ""}`} onClick={() => setCardId(v.rem_id)}>
                  <div className="rem-item-top">
                    <b>{e.pat_fio}</b>
                    <span className="rem-item-list">{e.list_name}</span>
                  </div>
                  <div className="rem-item-why">{v.due ? dueText(v) : waitText(v)}</div>
                  <div className="rem-item-meta">
                    {[
                      e.pat_phone,
                      [e.oper_type, e.eye].filter(Boolean).join(" "),
                      e.funding ? fundingLabel(e.funding) : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {v.note && <div className="rem-item-note">{v.note}</div>}
                  <div className="rem-item-foot">
                    поставлено {ruDate(v.created_at)}
                    {v.created_fio ? `, ${shortName(v.created_fio)}` : ""}
                    {other && <em className="rem-item-held">открыта у {shortName(other.fio)}</em>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rem-panel-foot">Поставить напоминание — в оперсписке: меню строки «⋮» → «Напомнить…»</div>
        </div>
      )}

      {cardId !== null && (
        <ReminderCard
          remId={cardId}
          user={user}
          onOpenPatient={
            onOpenPatient
              ? (id) => {
                  setOpen(false);
                  onOpenPatient(id);
                }
              : undefined
          }
          onClose={() => {
            setCardId(null);
            loadList();
          }}
        />
      )}
    </div>
  );
}
